const MAX_COMPARISON_PROPERTIES = 4;

const state = {
  comparisonProperties: [],
  isComparing: false,
};

const mutations = {
  ADD_TO_COMPARISON(state, property) {
    if (state.comparisonProperties.length < MAX_COMPARISON_PROPERTIES) {
      const exists = state.comparisonProperties.some(p => p.id === property.id);
      if (!exists) {
        state.comparisonProperties.push(property);
      }
    }
  },
  REMOVE_FROM_COMPARISON(state, propertyId) {
    state.comparisonProperties = state.comparisonProperties.filter(p => p.id !== propertyId);
  },
  CLEAR_COMPARISON(state) {
    state.comparisonProperties = [];
  },
  SET_COMPARING(state, isComparing) {
    state.isComparing = isComparing;
  },
};

const actions = {
  addToComparison({ commit, state }, property) {
    if (state.comparisonProperties.length >= MAX_COMPARISON_PROPERTIES) {
      throw new Error(`Solo puedes comparar hasta ${MAX_COMPARISON_PROPERTIES} propiedades a la vez`);
    }
    
    const exists = state.comparisonProperties.some(p => p.id === property.id);
    if (exists) {
      throw new Error('Esta propiedad ya está en la comparación');
    }
    
    commit('ADD_TO_COMPARISON', property);
    
    // Auto-save to localStorage
    localStorage.setItem('comparison_properties', JSON.stringify(state.comparisonProperties));
    
    return true;
  },
  
  removeFromComparison({ commit, state }, propertyId) {
    commit('REMOVE_FROM_COMPARISON', propertyId);
    
    // Auto-save to localStorage
    localStorage.setItem('comparison_properties', JSON.stringify(state.comparisonProperties));
    
    return true;
  },
  
  clearComparison({ commit }) {
    commit('CLEAR_COMPARISON');
    localStorage.removeItem('comparison_properties');
    return true;
  },
  
  loadComparisonFromStorage({ commit }) {
    try {
      const saved = localStorage.getItem('comparison_properties');
      if (saved) {
        const properties = JSON.parse(saved);
        properties.forEach(property => {
          commit('ADD_TO_COMPARISON', property);
        });
      }
    } catch (error) {
      console.error('Error loading comparison from storage:', error);
      localStorage.removeItem('comparison_properties');
    }
  },
  
  toggleComparing({ commit, state }) {
    commit('SET_COMPARING', !state.isComparing);
    return state.isComparing;
  },
};

const getters = {
  comparisonProperties: state => state.comparisonProperties,
  comparisonCount: state => state.comparisonProperties.length,
  isComparing: state => state.isComparing,
  canAddMore: state => state.comparisonProperties.length < MAX_COMPARISON_PROPERTIES,
  maxComparison: () => MAX_COMPARISON_PROPERTIES,
  isInComparison: state => propertyId => state.comparisonProperties.some(p => p.id === propertyId),
  
  // Calculated comparison data
  comparisonData: state => {
    if (state.comparisonProperties.length === 0) return null;
    
    const properties = state.comparisonProperties;
    
    // Calculate statistics for comparison
    const prices = properties.map(p => {
      // Use price history if available, otherwise fallback to precio
      if (p.price_history && p.price_history.length > 0) {
        return p.price_history[0].new_price;
      }
      return p.precio || 0;
    });
    
    const areas = properties.map(p => p.metros_cuadrados || p.totalArea || 0);
    const bedrooms = properties.map(p => p.habitaciones || 0);
    const bathrooms = properties.map(p => p.banos || 0);
    
    return {
      properties,
      stats: {
        price: {
          min: Math.min(...prices),
          max: Math.max(...prices),
          avg: prices.reduce((a, b) => a + b, 0) / prices.length,
        },
        area: {
          min: Math.min(...areas),
          max: Math.max(...areas),
          avg: areas.reduce((a, b) => a + b, 0) / areas.length,
        },
        bedrooms: {
          min: Math.min(...bedrooms),
          max: Math.max(...bedrooms),
          avg: bedrooms.reduce((a, b) => a + b, 0) / bedrooms.length,
        },
        bathrooms: {
          min: Math.min(...bathrooms),
          max: Math.max(...bathrooms),
          avg: bathrooms.reduce((a, b) => a + b, 0) / bathrooms.length,
        },
      },
    };
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
